import {
  ArrowRightLeft,
  AudioLines,
  Brain,
  CalendarCog,
  CalendarDays,
  FileCode2,
  GitBranch,
  ListChecks,
  Map as MapIcon,
  MessagesSquare,
  MessageSquareDot,
  NotebookPen,
  NotepadText,
  PhoneCall,
  Play,
  Redo2,
  ShieldCheck,
  UserPlus,
  Voicemail,
  Workflow,
  Wrench,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { DutyToolKey } from '@/features/duties/types'

export const CREATE_NEW_PATIENT_LABEL = 'Create New Patient'

/**
 * Single source of truth for the top toolbar's scroll-nav: every duty-detail
 * card in page order (DutyCallPage — left column top→bottom, then right). `icon`
 * must mirror the card's own header icon. `toolKey` present → toggleable tool
 * (in the popover; in the toolbar only while enabled); absent → static card
 * (always a toolbar link, never in the popover).
 */
export type DutyCardId =
  | 'fast_first_message'
  | 'conversation'
  | 'pre_call_texting'
  | 'post_call_texting'
  | 'facts'
  | 'voicemail'
  | 'facts_extraction'
  | 'labels'
  | 'resequence'
  | 'compiled_templates'
  | 'test_call'
  | 'connection'
  | 'patient_memory'
  | 'mercury:booking'
  | 'waypoints'
  | 'assistant_overrides'
  | 'authenticate_person'
  | 'transfer_call'
  | 'handoff_to_duty'
  | 'framework_tools'
  | 'encounter_note'
  | 'create_patient'

export type DutyCardNavEntry = {
  id: DutyCardId
  label: string
  icon: LucideIcon
  /** Toggleable tool key; omitted for static (always-present) cards. */
  toolKey?: DutyToolKey
}

export const DUTY_CARD_NAV: ReadonlyArray<DutyCardNavEntry> = [
  // Left column — script / prompt cards.
  { id: 'pre_call_texting', label: 'Pre-call Texting', icon: MessageSquareDot },
  {
    id: 'fast_first_message',
    label: 'Fast First Message',
    icon: Play,
    toolKey: 'fast_first_message',
  },
  { id: 'conversation', label: 'Conversation', icon: MessagesSquare },
  {
    id: 'post_call_texting',
    label: 'Post-call Texting',
    icon: MessageSquareDot,
  },
  { id: 'facts', label: 'Facts', icon: ListChecks },
  {
    id: 'voicemail',
    label: 'Voicemail',
    icon: Voicemail,
    toolKey: 'voicemail',
  },
  {
    id: 'facts_extraction',
    label: 'Fact Extraction',
    icon: NotebookPen,
    toolKey: 'facts_extraction',
  },
  { id: 'labels', label: 'Labels & Mapping', icon: MapIcon, toolKey: 'labels' },
  { id: 'resequence', label: 'Resequence', icon: Redo2, toolKey: 'resequence' },
  { id: 'compiled_templates', label: 'Compiled Templates', icon: FileCode2 },
  // Right column — tool / config cards.
  { id: 'test_call', label: 'Test call', icon: PhoneCall },
  { id: 'connection', label: 'Connection', icon: CalendarCog },
  {
    id: 'patient_memory',
    label: 'Memory',
    icon: Brain,
    toolKey: 'patient_memory',
  },
  {
    id: 'mercury:booking',
    label: 'Booking',
    icon: CalendarDays,
    toolKey: 'mercury:booking',
  },
  { id: 'waypoints', label: 'Waypoints', icon: GitBranch },
  { id: 'assistant_overrides', label: 'Assistant Overrides', icon: AudioLines },
  {
    id: 'authenticate_person',
    label: 'Authentication',
    icon: ShieldCheck,
    toolKey: 'authenticate_person',
  },
  {
    id: 'transfer_call',
    label: 'Transfer',
    icon: ArrowRightLeft,
    toolKey: 'transfer_call',
  },
  {
    id: 'handoff_to_duty',
    label: 'Handoff to Duty',
    icon: Workflow,
    toolKey: 'handoff_to_duty',
  },
  { id: 'framework_tools', label: 'Tools', icon: Wrench },
  // The slot a workflow-provided in-call tool occupies — see
  // WORKFLOW_TOOL_SLOTS. `label` is only the fallback: the pill and card both
  // prefer the workflow's own title, so this is what shows before the catalogue
  // loads (or when Nebula can't read the workflow's YAML). The toolKey names the
  // Mercury capability the workflow stands in for; the duty's Nebula
  // registration is what decides whether the pill and card are on
  // (see visibleToolsPills and selectedTools in DutyDetailContexts).
  {
    id: 'encounter_note',
    label: 'Encounter Note',
    icon: NotepadText,
    toolKey: 'mercury:encounter',
  },
  {
    id: 'create_patient',
    label: CREATE_NEW_PATIENT_LABEL,
    icon: UserPlus,
    toolKey: 'mercury:patient',
  },
]

/**
 * The matrix row that governs each card, for `DutyCardAnchor` to ask about.
 *
 * One place, so gating a card is a line here rather than a wrapper on each
 * component.
 *
 * Total on purpose: a new `DutyCardId` does not compile until it names a row
 * or says `null`. A card whose visibility nothing decides is how a surface
 * ships ungoverned, and a partial map let one be added by saying nothing.
 */
export const DUTY_CARD_CAPABILITY: Record<DutyCardId, string | null> = {
  fast_first_message: 'duty.fastFirstMessage',
  conversation: 'duty.conversation',
  pre_call_texting: 'duty.conversation',
  post_call_texting: 'duty.conversation',
  facts: 'duty.facts',
  voicemail: 'duty.voicemail',
  facts_extraction: 'duty.factExtraction',
  labels: 'duty.labels',
  resequence: 'duty.resequence',
  compiled_templates: 'duty.compiledTemplates',
  test_call: 'duty.testCall',
  connection: 'duty.connectionBase',
  waypoints: 'duty.waypoints',
  assistant_overrides: 'duty.assistantOverrides',
  authenticate_person: 'duty.authBase',
  transfer_call: 'duty.transfer',
  handoff_to_duty: 'duty.handoff',
  encounter_note: 'calls.encounterNote',
  patient_memory: 'duty.patientMemory',
  // Mercury tools the duty's own registration decides, not a role, so no row
  // governs them.
  'mercury:booking': null,
  create_patient: null,
  // The org tool builder, gated by the same Clerk org:global_admin check as
  // /tools rather than by a row — see UNGOVERNED in test_capability_catalog.
  framework_tools: null,
}

/** Tool entries only, in page order — drives the Tools popover toggle list. */
export const DUTY_TOOL_NAV: ReadonlyArray<
  DutyCardNavEntry & { toolKey: DutyToolKey }
> = DUTY_CARD_NAV.filter(
  (e): e is DutyCardNavEntry & { toolKey: DutyToolKey } => e.toolKey != null
)

/** Analysis configuration surfaced under "Post-Call Tools". */
const POST_CALL_ANALYSIS_PILL_KEYS: ReadonlyArray<DutyToolKey> = [
  'facts_extraction',
  'labels',
  'resequence',
]

export const CONFIGURATION_PILL_KEYS: ReadonlySet<DutyToolKey> = new Set(
  POST_CALL_ANALYSIS_PILL_KEYS
)

/**
 * Pills whose toggle never writes to app_duties.tools: the post-call
 * configuration pills and Fast First Message,
 * which the server derives as "on" from config.firstMessage (see
 * get_duty_tools_handler) rather than storing in the enum column. These toggle
 * card visibility locally, with the server-derived state as the default.
 * Exception: disabling Fast First Message while a message is saved clears
 * config.firstMessage (after confirmation) so the override actually stops
 * applying to calls.
 */
export const UNPERSISTED_PILL_KEYS: ReadonlySet<DutyToolKey> = new Set([
  ...POST_CALL_ANALYSIS_PILL_KEYS,
  'fast_first_message',
])

/**
 * The encounter-note workflow's slug. Matched by slug, not by trigger: Nebula
 * resolves `trigger` from the workflow-duty link, so it is always null on the
 * org listing. Slug is the stable identifier Nebula's invoke URL uses too.
 */
export const ENCOUNTER_WORKFLOW_SLUG = 'encounter-note'
export const NEW_PATIENT_WORKFLOW_SLUG = 'new-patient'

/**
 * Where a workflow-provided in-call tool lands on the call screen, keyed by
 * workflow slug.
 *
 * A workflow *asks* for an in-call tool by declaring `provides_in_call_tool` in
 * its YAML, and that flag is what decides whether the pill and card are offered
 * (see `inCallWorkflowTools` in DutyDetailContexts) — the label and card title
 * come from the workflow's own title, so nothing about naming it lives here.
 *
 * What does live here is everything the flag can't supply, because it isn't the
 * workflow's to decide:
 *   - `toolKey` — the pill's identity: which DutyToolKey the popover, the card
 *     gate and the PATCH body all speak in. The state itself lives in the Nebula
 *     registration, not in `app_duties.tools`, but the key still has to be one
 *     the tools PATCH accepts (PATCHABLE_DUTY_TOOL_KEYS server-side), which is
 *     why this map is not derived from the catalogue: a workflow that opts in
 *     with no entry here has no key to toggle under, so it gets no pill.
 *     `mercury:encounter` (submit_encounter_note) is the only one today; adding
 *     the next means a DutyToolKey, a server-side accept-list entry, and a line
 *     here.
 *   - `cardId` — the DUTY_CARD_NAV entry the card registers under, which fixes
 *     its icon and its place in the toolbar's scroll-nav.
 */
export const WORKFLOW_TOOL_SLOTS: ReadonlyArray<{
  slug: string
  toolKey: DutyToolKey
  cardId: DutyCardId
}> = [
  {
    slug: ENCOUNTER_WORKFLOW_SLUG,
    toolKey: 'mercury:encounter',
    cardId: 'encounter_note',
  },
  {
    slug: NEW_PATIENT_WORKFLOW_SLUG,
    toolKey: 'mercury:patient',
    cardId: 'create_patient',
  },
]

/**
 * Tool keys whose toggle *is* a Nova workflow registration: the PATCH writes the
 * registration and leaves `app_duties.tools` alone, and the pill reads its
 * on/off state back from that same registration. This map tells the optimistic
 * update which `workflows` cache entry to patch. Mirrors
 * TOOL_WORKFLOW_REGISTRATIONS server-side (see
 * hoku/server/handlers/duty_details/workflows.py) — keep the two in step.
 */
export const TOOL_WORKFLOW_SLUGS: Partial<Record<DutyToolKey, string>> =
  Object.fromEntries(WORKFLOW_TOOL_SLOTS.map((s) => [s.toolKey, s.slug]))
